import type { CustomFolder } from "@/types/documents";

// Mock data per custom folders
const mockCustomFolders: CustomFolder[] = [
    {
        id: "folder-1",
        name: "2024",
        parentId: null,
        containerIds: ["1", "2", "3", "4", "5"],
        createdAt: new Date("2024-01-01"),
        updatedAt: new Date("2024-01-01"),
    },
    {
        id: "folder-2",
        name: "2025",
        parentId: null,
        containerIds: ["19", "20", "21", "22", "23"],
        createdAt: new Date("2025-01-01"),
        updatedAt: new Date("2025-01-01"),
    },
    {
        id: "folder-3",
        name: "Q1 2024",
        parentId: "folder-1",
        containerIds: ["1", "2", "3"],
        createdAt: new Date("2024-01-15"),
        updatedAt: new Date("2024-01-15"),
    },
    {
        id: "folder-4",
        name: "Electronics",
        parentId: null,
        containerIds: ["1", "12"],
        createdAt: new Date("2024-01-20"),
        updatedAt: new Date("2024-01-20"),
    },
    {
        id: "folder-5",
        name: "Food Products",
        parentId: null,
        containerIds: ["2", "5", "6", "7"],
        createdAt: new Date("2024-01-25"),
        updatedAt: new Date("2024-01-25"),
    },
];

// In-memory storage
let customFolders: CustomFolder[] = [...mockCustomFolders];

// Service per gestire le custom folders
export const customFolderService = {
    // Get all folders
    getAll: (): CustomFolder[] => {
        return [...customFolders];
    },

    // Get folder by ID
    getById: (id: string): CustomFolder | undefined => {
        return customFolders.find((folder) => folder.id === id);
    },

    // Get folders by parent ID (per navigazione annidata)
    getByParentId: (parentId: string | null): CustomFolder[] => {
        return customFolders.filter((folder) => folder.parentId === parentId);
    },

    // Get root folders (parentId === null)
    getRootFolders: (): CustomFolder[] => {
        return customFolders.filter((folder) => folder.parentId === null);
    },

    // Get folder path (breadcrumb)
    getFolderPath: (folderId: string): CustomFolder[] => {
        const path: CustomFolder[] = [];
        let current: CustomFolder | undefined = customFolders.find((f) => f.id === folderId);

        while (current) {
            path.unshift(current);
            if (current.parentId) {
                current = customFolders.find((f) => f.id === current!.parentId);
            } else {
                current = undefined;
            }
        }

        return path;
    },

    // Create new folder
    create: (data: Omit<CustomFolder, "id" | "createdAt" | "updatedAt">): CustomFolder => {
        const newFolder: CustomFolder = {
            id: `folder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            ...data,
            createdAt: new Date(),
            updatedAt: new Date(),
        };
        customFolders.push(newFolder);
        return newFolder;
    },

    // Update folder
    update: (id: string, data: Partial<Omit<CustomFolder, "id" | "createdAt" | "updatedAt">>): CustomFolder | null => {
        const index = customFolders.findIndex((folder) => folder.id === id);
        if (index === -1) return null;

        customFolders[index] = {
            ...customFolders[index],
            ...data,
            updatedAt: new Date(),
        };
        return customFolders[index];
    },

    // Delete folder
    delete: (id: string): boolean => {
        // Verifica se ci sono sottocartelle
        const hasChildren = customFolders.some((folder) => folder.parentId === id);
        if (hasChildren) {
            // Non permettere eliminazione se ci sono sottocartelle
            return false;
        }

        const index = customFolders.findIndex((folder) => folder.id === id);
        if (index === -1) return false;

        customFolders.splice(index, 1);
        return true;
    },

    // Add container to folder
    addContainer: (folderId: string, containerId: string): boolean => {
        const folder = customFolders.find((f) => f.id === folderId);
        if (!folder) return false;

        if (!folder.containerIds.includes(containerId)) {
            folder.containerIds.push(containerId);
            folder.updatedAt = new Date();
        }
        return true;
    },

    // Remove container from folder
    removeContainer: (folderId: string, containerId: string): boolean => {
        const folder = customFolders.find((f) => f.id === folderId);
        if (!folder) return false;

        const index = folder.containerIds.indexOf(containerId);
        if (index > -1) {
            folder.containerIds.splice(index, 1);
            folder.updatedAt = new Date();
            return true;
        }
        return false;
    },

    // Get containers in folder (ricorsivo se include sottocartelle)
    getContainersInFolder: (folderId: string, includeChildren: boolean = false): string[] => {
        const folder = customFolders.find((f) => f.id === folderId);
        if (!folder) return [];

        let containerIds = [...folder.containerIds];

        if (includeChildren) {
            const childFolders = customFolders.filter((f) => f.parentId === folderId);
            childFolders.forEach((child) => {
                const childContainers = customFolderService.getContainersInFolder(child.id, true);
                containerIds = [...containerIds, ...childContainers];
            });
        }

        return [...new Set(containerIds)]; // Rimuovi duplicati
    },

    // Find which folder(s) contain a specific container
    getFoldersContainingContainer: (containerId: string): CustomFolder[] => {
        return customFolders.filter((folder) => folder.containerIds.includes(containerId));
    },
};

